import React, { useMemo, useState } from "react";
import { X } from "lucide-react";
import { TickerDropdown, TickerDropdownCategorySelection } from "./TickerDropdown";
import { labelCls } from "../../theme/styles/classes";
import styles from "./SymbolBank.module.css";

interface SymbolBankProps {
  symbolBank: string[];
  onAddSymbol: (symbol: string) => void;
  onAddSymbols: (symbols: string[]) => void;
  onClearSymbols: () => void;
  onRemoveSymbol: (idx: number) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

export const SymbolBank: React.FC<SymbolBankProps> = ({
  symbolBank,
  onAddSymbol,
  onAddSymbols,
  onClearSymbols,
  onRemoveSymbol,
  label = "Symbols",
  placeholder = "Search symbol, sector, or industry...",
  disabled = false,
}) => {
  const [pendingSymbol, setPendingSymbol] = useState("");

  const normalizedSymbols = useMemo(
    () => symbolBank.map((symbol) => symbol.trim().toUpperCase()).filter(Boolean),
    [symbolBank]
  );
  const normalizedSymbolSet = useMemo(() => new Set(normalizedSymbols), [normalizedSymbols]);

  const handleCommitSymbol = (rawSymbol: string) => {
    const normalizedPending = rawSymbol.trim().toUpperCase();
    if (!normalizedPending || normalizedSymbols.includes(normalizedPending)) {
      return;
    }

    onAddSymbol(normalizedPending);
    setPendingSymbol("");
  };

  const handleCommitCategory = ({ symbols }: TickerDropdownCategorySelection) => {
    const toAdd: string[] = [];
    const seen = new Set<string>();

    for (const rawSymbol of symbols) {
      const normalized = rawSymbol.trim().toUpperCase();
      if (!normalized || normalizedSymbolSet.has(normalized) || seen.has(normalized)) {
        continue;
      }
      seen.add(normalized);
      toAdd.push(normalized);
    }

    if (toAdd.length === 0) {
      setPendingSymbol("");
      return;
    }

    onAddSymbols(toAdd);
    setPendingSymbol("");
  };

  const isDuplicate = pendingSymbol.trim().toUpperCase() !== "" &&
                      normalizedSymbols.includes(pendingSymbol.trim().toUpperCase());

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <label className={labelCls}>{label}</label>
        <button
          type="button"
          className={styles.clearAllButton}
          onClick={onClearSymbols}
          disabled={disabled || symbolBank.length === 0}
          aria-label="Clear all symbols"
        >
          Clear all
        </button>
      </div>

      <div className={styles.inputRow}>
        <TickerDropdown
          value={pendingSymbol}
          onChange={setPendingSymbol}
          onCommit={handleCommitSymbol}
          enableCategorySelection
          onCategorySelect={handleCommitCategory}
          placeholder={placeholder}
          className={styles.inputDropdown}
          disabled={disabled}
        />
      </div>

      {isDuplicate && (
        <div className={styles.hintText}>Symbol already added</div>
      )}

      <div className={styles.tagsContainer}>
        {symbolBank.map((symbol, idx) => (
          <div key={`${symbol}-${idx}`} className={styles.tag}>
            <span className={styles.tagText}>{symbol}</span>
            <button
              type="button"
              className={styles.removeButton}
              disabled={disabled}
              onClick={() => onRemoveSymbol(idx)}
              aria-label={`Remove ${symbol}`}
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
